import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { TranslateModule } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { map, Observable, shareReplay, Subscription } from 'rxjs';
import { IPersonnelOnBoard } from 'src/app/crew-management-user/models/current-personnel/personnel-on-board.model';
import { LoadCurrentPersonnelForDepartment } from 'src/app/crew-management-user/state/current-personnel/current-personnel.actions';
import { CurrentPersonnelState } from 'src/app/crew-management-user/state/current-personnel/current-personnel.state';
import { IDepartmentUser } from 'src/app/department/models/department.model';
import { LoadUsersForDepartment } from 'src/app/department/state/department.actions';
import { DepartmentState } from 'src/app/department/state/department.state';
import { getDepartmentId } from 'src/app/services/store-snapshot.service';
import { DateTimeFormatDirective } from 'src/app/shared/directives/date-time-format.directive';
import { fullName } from 'src/app/shared/functions/full-name';
import { IdName } from 'src/app/shared/models/id-name';
import { IOption } from 'src/app/shared/models/option.model';
import { UserParticipant } from 'src/app/shared/models/user-participant.model';
import { UIElementsModule } from 'src/app/shared/ui-elements/ui-elements.module';
import { LoadAllExposureDurations } from 'src/app/storage-admin/state/exposure-duration.actions';
import { ExposureDurationState } from 'src/app/storage-admin/state/exposure-duration.state';
import { ChemicalExposurePlacement } from '../../enums/chemical-exposure-placement.enum';
import { ChemicalExposureForm } from '../../forms/chemical-exposure.form';

@Component({
  selector: 'app-chemical-exposure-fields',
  templateUrl: './chemical-exposure-fields.component.html',
  styleUrls: ['./chemical-exposure-fields.component.scss'],
  standalone: true,
  imports: [CommonModule, DateTimeFormatDirective, TranslateModule, UIElementsModule],
})
export class ChemicalExposureFieldsComponent implements OnInit, OnChanges, OnDestroy {
  @Input({ required: true }) form: ChemicalExposureForm;
  @Input() departmentId?: string;
  @Input() showIdentityFields = true;
  @Input() disabled = false;

  exposureDurations: IdName[] = [];
  showOnBoard = new FormControl(false, { nonNullable: true });
  readonly participantColumns = ['name', 'jobPosition'];
  readonly placementOptions: IOption[] = [
    { id: ChemicalExposurePlacement.Outside, name: 'outside' },
    { id: ChemicalExposurePlacement.Inside, name: 'inside' },
  ];

  private subscription = new Subscription();
  private personnelToggleRestored = false;
  private latestPersonnel: UserParticipant[] = [];

  currentPersonnel$: Observable<UserParticipant[]>;
  participants$: Observable<UserParticipant[]>;

  constructor(private store: Store) {}

  get isLocked(): boolean {
    return this.disabled || !!this.form?.disabled;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.disabled || changes.form) {
      this.syncPersonnelToggle();
    }
    if (changes.form && !changes.form.firstChange) {
      this.personnelToggleRestored = false;
      this.restorePersonnelToggle(this.latestPersonnel);
    }
  }

  ngOnInit(): void {
    const departmentId = this.departmentId || getDepartmentId();
    this.currentPersonnel$ = this.store
      .dispatch(new LoadCurrentPersonnelForDepartment(departmentId))
      .pipe(
        map(() =>
          this.store.selectSnapshot(
            CurrentPersonnelState.getCurrentPersonnelForDepartment(departmentId)
          )
        ),
        map((personnel) => this.mapPersonnel(personnel)),
        shareReplay({ bufferSize: 1, refCount: true })
      );
    this.participants$ = this.store
      .dispatch(new LoadUsersForDepartment(departmentId, { onlyEmployed: true }))
      .pipe(
        map(() => this.store.selectSnapshot(DepartmentState.getUsersForDepartment(departmentId))),
        map((users) => this.mapDepartmentUsers(users)),
        shareReplay({ bufferSize: 1, refCount: true })
      );
    this.syncPersonnelToggle();
    this.subscription.add(
      this.store.dispatch(new LoadAllExposureDurations()).subscribe({
        next: () => {
          this.exposureDurations =
            this.store.selectSnapshot(ExposureDurationState.getExposureDurationsList) ?? [];
        },
        error: () => {
          this.exposureDurations = [];
        },
      })
    );
    this.subscription.add(
      this.currentPersonnel$.subscribe((personnel) => {
        this.latestPersonnel = personnel;
        this.restorePersonnelToggle(personnel);
      })
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  onShowOnBoardChange(event: MatSlideToggleChange): void {
    this.showOnBoard.setValue(event.checked);
  }

  private restorePersonnelToggle(personnel: UserParticipant[]): void {
    const selectedIds = this.form?.exposedUserIds?.value ?? [];
    if (this.personnelToggleRestored || !selectedIds.length || !personnel.length) {
      return;
    }
    this.personnelToggleRestored = true;
    const onboardIds = new Set(personnel.map((person) => person.id));
    if (selectedIds.every((id) => onboardIds.has(id))) {
      this.showOnBoard.setValue(true);
    }
  }

  private syncPersonnelToggle(): void {
    if (this.isLocked) {
      this.showOnBoard.disable({ emitEvent: false });
      return;
    }
    this.showOnBoard.enable({ emitEvent: false });
  }

  private mapPersonnel(personnel: IPersonnelOnBoard[]): UserParticipant[] {
    return (personnel || []).map(({ id, name, position }) => ({
      id,
      name,
      jobPosition: position || '',
    }));
  }

  private mapDepartmentUsers(users: IDepartmentUser[]): UserParticipant[] {
    return (users || []).map((user) => ({
      id: user.id,
      name: fullName(user),
      jobPosition: user.employedAs,
    }));
  }
}
